import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLoaderData, useParams, useSearchParams } from 'react-router-dom';
import { useImmerReducer } from 'use-immer';

import CommitmentService from './CommitmentService';

import {
    getDomainValueForCode,
    getFlexColDomainValueForCode,
    isEmpty,
    isSuperAdmin,
} from '../../../library/Core/SmartFunctions';

import { GlobalContext } from '../../../library/Core/GlobalContext';
import SessionContext from '../../../library/Core/SessionContext';
import { SmartContext } from '../../../library/Core/SmartContext';
import smartReducer from '../../../library/Core/SmartReducer';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import { addUserSpecificDomains } from '../../../library/Service/domainService';
import { axiosClient } from '../../../library/Service/axiosClient';
import Swal from 'sweetalert2';

/* ---------------- LOCAL TYPES ---------------- */

type Willing = 'Yes' | 'No' | 'Not_Asked';

interface SignalState {
    willing: Willing;
    done_count: number;
    signal_notes: string;
}

type FormData = Record<string, Record<string, SignalState>>;

const EMPTY_STATE: SignalState = {
    willing: 'Not_Asked',
    done_count: 0,
    signal_notes: '',
};

const isTouched = (d?: SignalState) =>
    !!d && (d.willing !== 'Not_Asked' || d.done_count > 0 || !!d.signal_notes);

/* ---------------- COMPONENT ---------------- */

const CommitmentCapture = () => {
    const { instituteId = '', userId = '', adminCallLogId: routeCallLogId = '' } = useParams();
    const [searchParams] = useSearchParams();
    const { globalState } = useContext(GlobalContext);

    // When the call id is supplied as a route path segment (opened from an
    // existing outreach-log row), the call already exists — so the Call Session
    // selector and the click-to-call button are hidden and saves attach
    // directly to this id.
    const isCallSessionFromRoute = !!routeCallLogId;

    /* ---------------- LOADER DATA ---------------- */

    const { formConfig, data, routeInfo, domain: loadedDomain } =
        (useLoaderData() as State) || ({} as State);

    // adminCallLogId is the BIGINT id of the admin_call_log row this session
    // attaches its writes to. Resolved in priority order:
    //   1) URL deep-link `?adminCallLogId=…` (resume an in-progress session)
    //   2) Latest admin_call_log_id from this alumni's prior signals — so
    //      re-opening the form lets the agent fix/extend the last call's
    //      responses without forcing a new click-to-call
    //   3) Latest admin_call_log row for this alumnus, even if no signals
    //      were captured in it yet — lets the agent open the form against
    //      an existing call (the dropdown's visual default) and start
    //      adding signals straight away
    //   4) Set by the click-to-call response when the agent kicks off a
    //      fresh call (`POST /acefone/click-to-call/`)
    const [adminCallLogId, setAdminCallLogId] = useState<string | number | null>(() => {
        // 0) Route path segment — outreach-log-row entry, highest priority
        if (routeCallLogId) return routeCallLogId;
        const fromUrl = searchParams.get('adminCallLogId');
        if (fromUrl) return fromUrl;
        const rows = data?.priorSignals ?? [];
        if (rows.length) {
            const latestFromSignals = rows.reduce(
                (m: number, r: any) => Math.max(m, Number(r.adminCallLogId || 0)),
                0
            );
            if (latestFromSignals) return latestFromSignals;
        }
        const logs = data?.callLogs ?? [];
        if (logs.length && logs[0]?.id) return logs[0].id;
        return null;
    });

    // All call sessions for this (alumnus, institute). Loaded via the
    // pageLoader and used to populate the "Call Session" dropdown above the
    // Alumni Information card. Kept in state so the synthetic entry pushed
    // after a successful click-to-call shows up without a page refresh.
    const [callLogs, setCallLogs] = useState<any[]>(
        () => (data?.callLogs ?? []) as any[]
    );

    // True if this alumni has any captured signal history — drives whether
    // the form mounts on first render (review-mode) even without an active
    // call session. Sourced from CommitmentService.pageLoader → getByUser.
    const hasPriorData =
        Array.isArray(data?.priorSignals) && data.priorSignals.length > 0;

    /* ---------------- SMART CONTEXT ---------------- */

    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(
        smartReducer,
        {
            flags: { isDataLoading: true, showFormErrors: 0 },
            formValidationErrors: {},
            actions: {},
        } as any
    );

    /* ---------------- INJECT DATA + DOMAIN INTO SMART STATE ---------------- */

    useEffect(() => {
        const baseDomain =
            loadedDomain ?? new Map([...(globalState.domain || new Map())]);
        const domain = addUserSpecificDomains(new Map([...baseDomain]), data ?? {});

        dispatch({
            type: 'DATA_INIT',
            payload: {
                formConfig: formConfig ?? {},
                data: data ?? {},
                domain,
                routeInfo: routeInfo ?? { instituteId, userId },
            },
        });
    }, []);

    /* ---------------- INSTITUTE FLEXCOL → COMMITMENT PATHS + TAG ---------------- */
    // INSTITUTE_DOMAIN.flex_col example:
    //   { "client_type": "CLIENT", "is_published": "1", "founding_year": 1981,
    //     "institute_type": "UNIVERSITY",
    //     "commitment_paths": ["JP","CG","AT"], ... }

    const flexCol = getFlexColDomainValueForCode(
        String(instituteId),
        'INSTITUTE_DOMAIN',
        state
    );
    // Memoize on the raw flexCol string so the parsed object / paths array keep
    // a stable reference across renders. Without this, JSON.parse produces a new
    // object every render, which invalidates the `goals` useMemo every render and
    // drives the level-expand effect into an infinite setState loop.
    const instituteDomain = useMemo(
        () => (!isEmpty(flexCol) ? JSON.parse(flexCol) : {}),
        [flexCol]
    );
    const enabledCommitmentPaths: string[] = useMemo(
        () => instituteDomain?.commitment_paths || [],
        [instituteDomain]
    );

    const instituteTag: 'college' | 'k12' = useMemo(() => {
        const t = String(instituteDomain?.institute_type || '').toUpperCase();
        if (t === 'SCHOOL' || t === 'K12_SCHOOL' || t === 'PRE_SCHOOL') return 'k12';
        return 'college';
    }, [instituteDomain?.institute_type]);

    /* ---------------- BUILD GOALS / LEVELS / SIGNALS FROM DOMAIN ---------------- */

    const goals = useMemo(() => {
        const pathwayGoals = state.domain?.get('PATHWAY_GOAL') ?? [];
        const pathwayLevels = state.domain?.get('PATHWAY_LEVEL') ?? [];
        const pathwaySignals = state.domain?.get('PATHWAY_SIGNAL') ?? [];

        return [...pathwayGoals]
            .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
            .map((g: any) => {
                const goalMeta = !isEmpty(g.flexCol) ? JSON.parse(g.flexCol) : {};

                const levels = pathwayLevels
                    .filter((l: any) => l.parentCode === g.code)
                    .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
                    .map((l: any, idx: number) => {
                        const levelMeta = !isEmpty(l.flexCol)
                            ? JSON.parse(l.flexCol)
                            : {};

                        const signals = pathwaySignals
                            .filter((s: any) => s.parentCode === l.code)
                            .map((s: any) => {
                                const sMeta = !isEmpty(s.flexCol)
                                    ? JSON.parse(s.flexCol)
                                    : {};
                                return {
                                    code: s.code,
                                    label: s.value,
                                    institute_tag: sMeta.institute_tag || 'all',
                                    level_number:
                                        sMeta.level_number || levelMeta.level_number,
                                };
                            })
                            // visibility cascade: institute_tag must match (all|tag)
                            .filter(
                                (s: any) =>
                                    s.institute_tag === 'all' ||
                                    s.institute_tag === instituteTag
                            )
                            .sort(
                                (a: any, b: any) => a.displayOrder - b.displayOrder
                            );

                        return {
                            number: levelMeta.level_number || idx + 1,
                            code: l.code,
                            name: l.value,
                            signals,
                        };
                    })
                    .filter((l: any) => l.signals.length > 0);

                return {
                    code: g.code,
                    name: g.value,
                    enabled: enabledCommitmentPaths.includes(g.code),
                    theme: {
                        icon: goalMeta.icon,
                        color_primary: goalMeta.color_primary,
                        color_mid: goalMeta.color_mid,
                        color_light: goalMeta.color_light,
                    },
                    levels,
                };
            })
            .filter((g: any) => g.levels.length > 0);
    }, [state.domain, enabledCommitmentPaths, instituteTag]);

    // Only enabled (commitment_paths configured) goals are selectable /
    // drive the capture form. Disabled ones still render as greyed-out tabs.
    const enabledGoals = useMemo(
        () => goals.filter((g: any) => g.enabled),
        [goals]
    );

    /* ---------------- ACTIVE GOAL + FORM STATE ---------------- */

    const [activeGoal, setActiveGoal] = useState('');
    const [expandedLevels, setExpandedLevels] = useState<Record<string, boolean>>(
        {}
    );
    const [formData, setFormData] = useState<FormData>({});
   // ✅ No extra fetch — seed callNotes from priorSignals directly
    const [callNotes, setCallNotes] = useState<string>(() => {
        const rows: any[] = data?.priorSignals ?? [];
        if (!adminCallLogId || !rows.length) return '';
        const match = rows.find(
            (r) => String(r.adminCallLogId) === String(adminCallLogId) && r.callNotes
        );
        return match?.callNotes || '';
    });
    const [saving, setSaving] = useState(false);

    /* ---------------- CLICK-TO-CALL (mirrors OutreachLog.tsx) ---------------- */

    const { sessionState } = useContext(SessionContext);

    const [showClickToCallModal, setShowClickToCallModal] = useState(false);
    const [clickToCallForm, setClickToCallForm] = useState({
        team: '',
        purposeCategory: '',
        purposeSubCategory: '',
        callMode: '',
    });

    // Parse adminAdditionalInfo from sessionState (same pattern as OutreachLog).
    const adminAdditionalInfo = useMemo(() => {
        try {
            if (!sessionState?.adminAdditionalInfo) return null;
            return JSON.parse(sessionState.adminAdditionalInfo);
        } catch (e) {
            console.error('Error parsing adminAdditionalInfo:', e);
            return null;
        }
    }, [sessionState]);

    const isClickToCallAllowed = useMemo(
        () => adminAdditionalInfo?.allow_click_to_call === true,
        [adminAdditionalInfo]
    );

    // Sub-categories filtered by the selected Purpose Category.
    const filteredSubCategories = useMemo(() => {
        if (!clickToCallForm.purposeCategory) return [];
        const all =
            state?.domain?.get('OUTREACH_PURPOSE_SUBCATEGORY_CODE') ?? [];
        return all.filter(
            (sc: any) => sc.parentCode === clickToCallForm.purposeCategory
        );
    }, [clickToCallForm.purposeCategory, state?.domain]);

    const handleClickToCallModalClose = () => {
        setShowClickToCallModal(false);
        setClickToCallForm({
            team: '',
            purposeCategory: '',
            purposeSubCategory: '',
            callMode: '',
        });
    };

    const handleClickToCall = () => {
        // Prefill from the agent's adminAdditionalInfo, same as OutreachLog.
        const teamValue = adminAdditionalInfo?.team || '';
        const callModeValue =
            adminAdditionalInfo?.call_mode === 'extension'
                ? 'extension'
                : adminAdditionalInfo?.call_mode || '';
        setClickToCallForm({
            team: teamValue,
            purposeCategory: '',
            purposeSubCategory: '',
            callMode: callModeValue,
        });
        setShowClickToCallModal(true);
    };

    const handleClickToCallFormSubmit = () => {
        const f = clickToCallForm;
        if (!f.team || !f.purposeCategory || !f.purposeSubCategory || !f.callMode) {
            Swal.fire(
                'Validation Error',
                'Please fill all required fields: Team, Purpose Category, Sub Category, and Call Mode.',
                'error'
            );
            return;
        }

        const alumni = state.data?.alumniProfile ?? data?.alumniProfile ?? {};
        const phoneNumber =
            (alumni.mobileCountryCode || '') + (alumni.mobileNumber || '');
        if (!alumni.mobileNumber) {
            Swal.fire('No phone on file', 'This alumni has no mobile number registered.', 'warning');
            return;
        }

        setShowClickToCallModal(false);

        Swal.fire({
            title: 'Make a Call?',
            text: `This will initiate a call to ${alumni.aName || 'this alumni'}. Do you want to proceed?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, call now',
            cancelButtonText: 'No, cancel',
        }).then((result) => {
            if (!result.isConfirmed) return;

            Swal.fire({
                title: 'Initiating Call...',
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
            });

            // agentNumber resolution mirrors OutreachLog.tsx exactly.
            let agentNumber = (sessionState as any)?.mobileNumber;
            if (f.callMode === 'extension' && adminAdditionalInfo?.acefone_agent_id) {
                agentNumber = adminAdditionalInfo.acefone_agent_id;
            } else if (f.callMode === 'phone' && adminAdditionalInfo?.agent_mobile_number) {
                agentNumber = adminAdditionalInfo.agent_mobile_number;
            }

            axiosClient()
                .post(`${process.env.REACT_APP_COMMON_API}/acefone/click-to-call/`, {
                    agentNumber,
                    phoneNumber,
                    studentId: userId,
                    instituteId: Number(instituteId),
                    team: f.team,
                    purposeCategory: f.purposeCategory,
                    purposeSubCategory: f.purposeSubCategory,
                })
                .then((res: any) => {
                    Swal.close();
                    // Backend response shape:
                    //   { message: 'Click-to-call initiated successfully',
                    //     success: true, adminCallLogId: 482917, ...result }
                    // Capture adminCallLogId so the rest of this session's
                    // signal writes attach to the same admin_call_log row.
                    const newId =
                        res?.data?.adminCallLogId ??
                        res?.data?.data?.adminCallLogId ??
                        null;
                    if (newId) {
                        setAdminCallLogId(newId);
                        // Prepend a synthetic call_log row so the new call
                        // shows up in the Call Session dropdown without a
                        // page refresh. The disposition fields (comment,
                        // user_comments, talked_to, duration) get filled by
                        // the acefone webhook later — leave them blank here.
                        setCallLogs((prev) => [
                            {
                                id: newId,
                                created_at: new Date().toISOString(),
                                created_by:
                                    [
                                        (sessionState as any)?.firstName,
                                        (sessionState as any)?.lastName,
                                    ]
                                        .filter(Boolean)
                                        .join(' ') || 'You',
                                mode: 'ACEFONE_CLICK_TO_CALL',
                                purpose_category: f.purposeCategory,
                                purpose_subcat: f.purposeSubCategory,
                                comment: '',
                                user_comments: '',
                            },
                            ...prev,
                        ]);
                    }

                    Swal.fire({
                        position: 'top-end',
                        icon: 'success',
                        title: 'Call initiated successfully!',
                        showConfirmButton: false,
                        timer: 3000,
                        toast: true,
                    });
                    setClickToCallForm({
                        team: '',
                        purposeCategory: '',
                        purposeSubCategory: '',
                        callMode: '',
                    });
                })
                .catch((err: any) => {
                    console.error('Error initiating call:', err);
                    Swal.fire(
                        '',
                        err?.data?.error ||
                            err?.message ||
                            'An error occurred while initiating the call. Please try again.',
                        'error'
                    );
                });
        });
    };

    useEffect(() => {
        if (enabledGoals.length && !activeGoal) {
            setActiveGoal(enabledGoals[0].code);
        }
    }, [enabledGoals, activeGoal]);

    // default-expand all levels of the active goal.
    // Uses a functional update that returns the SAME reference when no level
    // needs expanding, so React bails out of the re-render. Without this guard
    // the effect schedules a fresh object every pass — and because `goals` is a
    // memo whose identity can change, that re-render re-runs the effect, which
    // sets state again → "Maximum update depth exceeded".
    useEffect(() => {
        const goal = enabledGoals.find((g: any) => g.code === activeGoal);
        if (!goal) return;
        setExpandedLevels((prev) => {
            let changed = false;
            const next: Record<string, boolean> = { ...prev };
            goal.levels.forEach((l: any) => {
                if (next[l.code] === undefined) {
                    next[l.code] = true;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [activeGoal, enabledGoals]);

    /* ---------------- SEED FORM FROM LOADER ---------------- */

    // Captured signals are user-wise + institute-wise (latest call per signal
    // from getByUser), NOT filtered by admin_call_log_id. The Call Session
    // dropdown only sets which call new saves attach to — it does not change
    // the displayed data.
    useEffect(() => {
        const rows = state.data?.priorSignals ?? data?.priorSignals ?? [];
        if (!rows.length) return;

        const seed: FormData = {};
        rows.forEach((row: any) => {
            if (!seed[row.goalCode]) seed[row.goalCode] = {};
            seed[row.goalCode][row.signalCode] = {
                willing: row.willing,
                done_count: Number(row.doneCount || 0),
                signal_notes: row.signalNotes || '',
            };
        });
        setFormData(seed);
    }, [state.data, data]);

    /* ---------------- HELPERS ---------------- */

    // "20 May 2026 14:32 — Aman Singh — Career Counseling — Discussed referrals…"
    // Each segment is omitted if blank. user_comments preferred over comment
    // (the agent's free text is more useful than the disposition code).
    const formatCallLogLabel = (log: any): string => {
        const d = log?.created_at ? new Date(log.created_at) : null;
        const date = d && !isNaN(d.getTime())
            ? `${d.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
              })} ${d.toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
              })}`
            : '';
        const agent = (log?.created_by || '').trim();
        const purpose = (log?.purpose_category || log?.purpose_subcat || '').trim();
        const cmt = (log?.user_comments || log?.comment || '').trim();
        const cmtShort = cmt.length > 40 ? `${cmt.slice(0, 40)}…` : cmt;
        return [date, agent, purpose, cmtShort].filter(Boolean).join(' — ');
    };

    const getSignalState = useCallback(
        (goalCode: string, signalCode: string) =>
            formData?.[goalCode]?.[signalCode] || EMPTY_STATE,
        [formData]
    );

    const setSignalField = useCallback(
        (goalCode: string, signalCode: string, patch: Partial<SignalState>) => {
            setFormData((prev) => ({
                ...prev,
                [goalCode]: {
                    ...(prev[goalCode] || {}),
                    [signalCode]: {
                        ...(prev[goalCode]?.[signalCode] || EMPTY_STATE),
                        ...patch,
                    },
                },
            }));
        },
        []
    );

    const toggleLevel = (levelCode: string) =>
        setExpandedLevels((prev) => ({ ...prev, [levelCode]: !prev[levelCode] }));

    /* ---------------- PROGRESS COUNTS ---------------- */

    const counts = useMemo(() => {
        let totalSignals = 0;
        let touchedSignals = 0;
        const perGoal: Record<string, { total: number; done: number }> = {};

        enabledGoals.forEach((g: any) => {
            perGoal[g.code] = { total: 0, done: 0 };
            g.levels.forEach((l: any) =>
                l.signals.forEach((s: any) => {
                    totalSignals++;
                    perGoal[g.code].total++;
                    const d = formData[g.code]?.[s.code];
                    if (isTouched(d)) {
                        touchedSignals++;
                        perGoal[g.code].done++;
                    }
                })
            );
        });

        const pct =
            totalSignals === 0
                ? 0
                : Math.round((touchedSignals / totalSignals) * 100);
        return { totalSignals, touchedSignals, pct, perGoal };
    }, [enabledGoals, formData]);

    /* ---------------- ACTIONS ---------------- */

    const onClear = () => {
        setFormData({});
        setCallNotes('');
    };

    const onSave = async () => {
        const signals: any[] = [];

        enabledGoals.forEach((goal: any) => {
            goal.levels.forEach((level: any) => {
                level.signals.forEach((signal: any) => {
                    const d = formData[goal.code]?.[signal.code];
                    if (!isTouched(d)) return;
                    signals.push({
                        signal_code: signal.code,
                        willing: d.willing,
                        done_count: d.done_count,
                        signal_notes: d.signal_notes || null,
                    });
                });
            });
        });

        if (!signals.length) {
            alert('Nothing to save');
            return;
        }

        setSaving(true);
        try {
            await CommitmentService.save({
                adminCallLogId: Number(adminCallLogId),
                userId,
                instituteId: Number(instituteId),
                signals,
                callNotes,
            });
            // alert('Saved successfully');
            await Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Saved successfully!',
            });
        } catch (err: any) {
             await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err ? `${err?.message}` : 'Failed to send message.',
            });
        }
        setSaving(false);
    };

    /* ---------------- UI ---------------- */

    if (state.flags?.isDataLoading) {
        return <div className="p-4">Loading...</div>;
    }

    if (!enabledGoals.length) {
        return (
            <div className="card p-5 text-center m-4">
                No commitment paths enabled for this institute
            </div>
        );
    }

    const goal = enabledGoals.find((g: any) => g.code === activeGoal) || enabledGoals[0];
    const alumni = state.data?.alumniProfile ?? data?.alumniProfile ?? {};

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            <div className="container-fluid py-3" style={{ maxWidth: 1200 }}>

                {/* =================== HEADER CARD =================== */}
                <div
                    className="rounded-4 card border-0 shadow-sm mb-4"
                    style={{
                        background:
                            'linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #166534 100%)',
                        color: '#fff',
                    }}
                >
                    <div className="card-body p-4 p-lg-5">
                        <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 align-items-lg-center">
                            <div>
                                <div className="text-uppercase text-white text-opacity-75 fw-medium mb-2 fs-6">
                                    Capture Progress
                                </div>
                                <h2 className="fw-bold mb-2 fs-1">Alumni Commitment Capture</h2>
                                <p className="text-white text-opacity-75 mb-0">
                                    Institute:{' '}
                                    <span className="fw-medium text-white text-opacity-75">
                                        {getDomainValueForCode(
                                            String(instituteId),
                                            'INSTITUTE_DOMAIN',
                                            state
                                        )}{' '}
                                        (
                                        {instituteTag === 'k12'
                                            ? 'K-12 School'
                                            : 'College / University'}
                                        )
                                    </span>
                                </p>
                            </div>

                            <div
                                className="d-flex flex-column gap-3 align-items-lg-end"
                                style={{ minWidth: 280 }}
                            >
                                {/* Read-only institute_type chips (derived from INSTITUTE_DOMAIN.flex_col.institute_type)
                                <div
                                    className="d-flex flex-wrap gap-2 justify-content-lg-end"
                                    id="inst-toggle"
                                >
                                    <button
                                        type="button"
                                        className="btn btn-sm rounded-pill px-3 py-2 fw-semibold"
                                        data-inst="college"
                                        disabled
                                        style={
                                            instituteTag === 'college'
                                                ? {
                                                      fontSize: 13,
                                                      background: '#166534',
                                                      borderColor: '#166534',
                                                      color: '#fff',
                                                      boxShadow:
                                                          '0 10px 24px rgba(22,101,52,.18)',
                                                  }
                                                : {
                                                      fontSize: 13,
                                                      background: '#fff',
                                                      borderColor: '#d1d5db',
                                                      color: '#475569',
                                                  }
                                        }
                                    >
                                        College / University
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm rounded-pill px-3 py-2 fw-semibold"
                                        data-inst="k12"
                                        disabled
                                        style={
                                            instituteTag === 'k12'
                                                ? {
                                                      fontSize: 13,
                                                      background: '#166534',
                                                      borderColor: '#166534',
                                                      color: '#fff',
                                                      boxShadow:
                                                          '0 10px 24px rgba(22,101,52,.18)',
                                                  }
                                                : {
                                                      fontSize: 13,
                                                      background: '#fff',
                                                      borderColor: '#d1d5db',
                                                      color: '#475569',
                                                  }
                                        }
                                    >
                                        K-12 School
                                    </button>
                                </div>
                                */}

                                <div className="w-100" style={{ maxWidth: 280 }}>
                                    <div className="d-flex justify-content-between small text-white mb-2">
                                        <span>Completed</span>
                                        <span className="fw-semibold text-white">
                                            {counts.touchedSignals}/{counts.totalSignals}
                                        </span>
                                    </div>
                                    <div
                                        className="progress"
                                        role="progressbar"
                                        aria-valuenow={counts.pct}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        style={{ height: 7 }}
                                    >
                                        <div
                                            className="progress-bar bg-success"
                                            style={{ width: `${counts.pct}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* =================== CALL SESSION SELECTOR =================== */}
                {/* Lets the agent attach their edits to a previous call_log
                    (amend an earlier capture) or to the call they just
                    initiated via click-to-call. Hidden when no calls exist
                    yet — the empty state below handles first-time capture. */}
                {!isCallSessionFromRoute && callLogs.length > 0 && (
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-body p-4">
                            <label className="form-label fw-semibold mb-2">
                                Call Session
                            </label>
                            <select
                                className="form-select"
                                value={adminCallLogId ?? ''}
                                onChange={(e) =>
                                    setAdminCallLogId(
                                        e.target.value ? e.target.value : null
                                    )
                                }
                            >
                                {callLogs.map((log: any) => (
                                    <option key={log.id} value={log.id}>
                                        {formatCallLogLabel(log)}
                                    </option>
                                ))}
                            </select>
                            <div className="form-text">
                                Edits attach to the selected call session.
                                Pick a previous call to amend it, or start a
                                new one with the phone button below.
                            </div>
                        </div>
                    </div>
                )}

                {/* =================== ALUMNI INFO CARD =================== */}
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body p-4">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                            <h3 className="h5 mb-0">Alumni Information</h3>
                            {/* Call button shows only when the route has no adminCallLogId
                                (i.e. not opened from an existing outreach-log row). */}
                            {!isCallSessionFromRoute && isSuperAdmin() && (
                                <div className="d-flex align-items-center gap-2">
                                    <a
                                        href="javascript:;"
                                        id="click-to-call-btn"
                                        className="btn btn-green text-decoration-none rounded-2 px-2 height-45"
                                        onClick={handleClickToCall}
                                        title="Click to call this alumni"
                                    >
                                      <span><i className="bi bi-telephone-outbound"></i></span>
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Read-only display — values are prefilled from the
                            alumni profile and not editable in this card. */}
                        <div className="row g-3">
                            <div className="col-12 col-md-6 col-xl-3">
                                <div className="text-uppercase small fw-semibold text-secondary mb-1">
                                    Full Name
                                </div>
                                <div
                                    data-alumni="aName"
                                    style={{ fontSize: 14, color: '#0f172a' }}
                                >
                                    {alumni.aName || '—'}
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-xl-3">
                                <div className="text-uppercase small fw-semibold text-secondary mb-1">
                                    Batch / Year
                                </div>
                                <div
                                    data-alumni="aBatch"
                                    style={{ fontSize: 14, color: '#0f172a' }}
                                >
                                    {alumni.aBatch || '—'}
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-xl-3">
                                <div className="text-uppercase small fw-semibold text-secondary mb-1">
                                    Program
                                </div>
                                <div
                                    data-alumni="aProgram"
                                    style={{ fontSize: 14, color: '#0f172a' }}
                                >
                                    {alumni.programName || '—'}
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-xl-3">
                                <div className="text-uppercase small fw-semibold text-secondary mb-1">
                                    Phone / Email
                                </div>
                                <div
                                    data-alumni="aPhone"
                                    style={{ fontSize: 14, color: '#0f172a' }}
                                >
                                    {alumni.aPhone || '—'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* =================== PRE-CALL EMPTY STATE =================== */}
                {/* First-time capture (no prior signals, no active call yet):
                    show only the empty-state CTA pointing back at the phone
                    button. As soon as the alumni has any captured history
                    OR the agent starts a call, the form mounts below so
                    earlier answers are visible while a new call is dialing. */}
                {!adminCallLogId && !hasPriorData && isSuperAdmin() && (
                    <div
                        className="card border-0 shadow-sm text-center p-5"
                        style={{ background: '#f8fafc' }}
                    >
                        <i
                            className="bi bi-telephone-outbound"
                            style={{ fontSize: 36, color: '#94a3b8' }}
                        />
                        <h5 className="mt-3 mb-1">Start the call to begin capture</h5>
                        <p className="text-secondary mb-0">
                            Click the green phone button in the Alumni
                            Information card to initiate a call. Signal capture
                            opens once the call is connected.
                        </p>
                    </div>
                )}

                {/* Form mounts whenever there's something to show — either
                    prior captures to review or a fresh call session to fill. */}
                {(adminCallLogId || hasPriorData) && (
                  <>
                {/* =================== GOAL TAB STRIP =================== */}
                {/* Active tab sits flush against the goal panel below
                    (panel has borderRadius 0 12px 12px 12px → flat top-left). */}
                <div className="d-flex gap-1 flex-wrap mb-0">
                    {goals.map((g: any) => {
                        const isActive = g.code === activeGoal;
                        const primary = g.theme?.color_primary || '#166534';
                        const isDisabled = !g.enabled;
                        return (
                            <button
                                key={g.code}
                                type="button"
                                aria-disabled={isDisabled}
                                title={
                                    isDisabled
                                        ? 'As per your contract and the project plan, this goal is out of scope currently. Please contact your success manager.'
                                        : undefined
                                }
                                onClick={() => {
                                    if (isDisabled) return;
                                    setActiveGoal(g.code);
                                }}
                                className="btn fw-semibold d-flex align-items-center gap-1"
                                style={{
                                    background: isDisabled
                                        ? '#f1f5f9'
                                        : isActive
                                        ? primary
                                        : '#e2e8f0',
                                    color: isDisabled
                                        ? '#94a3b8'
                                        : isActive
                                        ? '#fff'
                                        : '#4b5563',
                                    padding: '7px 13px',
                                    borderRadius: '8px 8px 0 0',
                                    border: 'none',
                                    fontSize: 11.5,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    opacity: isDisabled ? 0.6 : 1,
                                }}
                            >
                                {g.name}
                            </button>
                        );
                    })}
                </div>

                {/* =================== ACTIVE GOAL PANEL =================== */}
                <div
                    className="shadow-sm mb-3"
                    style={{
                        background: '#fff',
                        borderRadius: '0 12px 12px 12px',
                        padding: 18,
                        border: `2px solid ${goal.theme?.color_primary || '#166534'}`,
                        borderTop: `3px solid ${goal.theme?.color_primary || '#166534'}`,
                    }}
                >
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <h3
                            className="fw-bold m-0"
                            style={{ fontSize: 16, color: goal.theme?.color_primary || '#0f3d23' }}
                        >
                            {goal.name}
                        </h3>
                    </div>

                    <div>
                        {goal.levels.map((level: any) => {
                            const expanded = expandedLevels[level.code] ?? true;
                            // L1 → 0.6, L2 → 0.8, L3 → 1.0 (matches prototype visual cue)
                            const lvlOpacity = Math.min(1, 0.4 + 0.2 * level.number);

                            return (
                                <div
                                    key={level.code}
                                    className="mb-1"
                                    style={{
                                        border: '1px solid #e8ecf1',
                                        borderRadius: 10,
                                        overflow: 'hidden',
                                    }}
                                >
                                    {/* level header (accordion toggle) */}
                                    <div
                                        onClick={() => toggleLevel(level.code)}
                                        className="d-flex align-items-center justify-content-between p-2 ps-3 pe-3"
                                        style={{
                                            cursor: 'pointer',
                                            background: expanded
                                                ? goal.theme?.color_light || '#e8f5e9'
                                                : '#fafbfc',
                                            borderLeft: `4px solid ${
                                                goal.theme?.color_primary || '#166534'
                                            }`,
                                            borderRadius: 8,
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <span
                                                className="badge"
                                                style={{
                                                    background:
                                                        goal.theme?.color_primary || '#166534',
                                                    color: '#fff',
                                                    opacity: lvlOpacity,
                                                    padding: '2px 9px',
                                                    borderRadius: 5,
                                                    fontSize: 10.5,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                L{level.number}
                                            </span>
                                            <span className="fw-bold" style={{ fontSize: 13.5 }}>
                                                {level.name}
                                            </span>
                                            <span style={{ fontSize: 11.5, color: '#9ca3af' }}>
                                                ({level.signals.length})
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span
                                                className="d-inline-block"
                                                style={{
                                                    color: '#9ca3af',
                                                    fontSize: 13,
                                                    transition: 'transform .2s',
                                                    transform: expanded
                                                        ? 'rotate(180deg)'
                                                        : 'none',
                                                }}
                                            >
                                                ▼
                                            </span>
                                        </div>
                                    </div>

                                    {/* signal table */}
                                    {expanded && (
                                        <div style={{ padding: '4px 14px 14px' }}>
                                            {/* header row */}
                                            <div
                                                className="d-grid gap-1 pb-1 mb-1"
                                                style={{
                                                    gridTemplateColumns: '1fr 120px 100px 1fr',
                                                    borderBottom: '1px solid #f1f5f9',
                                                }}
                                            >
                                                <span
                                                    className="text-uppercase fw-bold"
                                                    style={{
                                                        fontSize: 9.5,
                                                        color: '#9ca3af',
                                                        letterSpacing: '.04em',
                                                    }}
                                                >
                                                    Signal
                                                </span>
                                                <span
                                                    className="text-uppercase fw-bold"
                                                    style={{
                                                        fontSize: 9.5,
                                                        color: '#9ca3af',
                                                        letterSpacing: '.04em',
                                                    }}
                                                >
                                                    Willing?
                                                </span>
                                                <span
                                                    className="text-uppercase fw-bold"
                                                    style={{
                                                        fontSize: 9.5,
                                                        color: '#9ca3af',
                                                        letterSpacing: '.04em',
                                                    }}
                                                >
                                                    Times Done
                                                </span>
                                                <span
                                                    className="text-uppercase fw-bold"
                                                    style={{
                                                        fontSize: 9.5,
                                                        color: '#9ca3af',
                                                        letterSpacing: '.04em',
                                                    }}
                                                >
                                                    Notes
                                                </span>
                                            </div>

                                            {/* signal rows */}
                                            {level.signals.map((signal: any) => {
                                                const sd = getSignalState(goal.code, signal.code);
                                                return (
                                                    <div
                                                        key={signal.code}
                                                        className="d-grid gap-1 align-items-center"
                                                        style={{
                                                            gridTemplateColumns:
                                                                '1fr 120px 100px 1fr',
                                                            padding: '8px 4px',
                                                            borderBottom:
                                                                '1px solid #fafbfc',
                                                        }}
                                                    >
                                                        {/* label + inline tag */}
                                                        <div
                                                            style={{
                                                                fontSize: 12.5,
                                                                lineHeight: 1.4,
                                                            }}
                                                        >
                                                            {signal.label}
                                                            {signal.institute_tag !== 'all' && (
                                                                <span
                                                                    className={
                                                                        'badge rounded-pill ms-2 ' +
                                                                        (signal.institute_tag === 'k12'
                                                                            ? 'text-bg-warning'
                                                                            : 'text-bg-primary')
                                                                    }
                                                                >
                                                                    {signal.institute_tag === 'k12'
                                                                        ? 'K-12'
                                                                        : 'College'}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* willing */}
                                                        <div className="d-flex gap-1">
                                                            {(
                                                                [
                                                                    ['Yes', 'Yes'],
                                                                    ['No', 'No'],
                                                                    ['Not_Asked', '-'],
                                                                ] as [Willing, string][]
                                                            ).map(([w, label]) => {
                                                                const active = sd.willing === w;
                                                                let cls =
                                                                    'btn btn-sm btn-outline-secondary';
                                                                if (active) {
                                                                    cls =
                                                                        w === 'Yes'
                                                                            ? 'btn btn-sm btn-success'
                                                                            : w === 'No'
                                                                            ? 'btn btn-sm btn-danger'
                                                                            : 'btn btn-sm btn-secondary';
                                                                }
                                                                return (
                                                                    <button
                                                                        key={w}
                                                                        type="button"
                                                                        className={cls}
                                                                        style={{
                                                                            fontSize: 10.5,
                                                                            fontWeight: 600,
                                                                            padding: '4px 8px',
                                                                        }}
                                                                        onClick={() =>
                                                                            setSignalField(
                                                                                goal.code,
                                                                                signal.code,
                                                                                { willing: w }
                                                                            )
                                                                        }
                                                                    >
                                                                        {label}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* times done */}
                                                        <div className="d-flex align-items-center gap-1">
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary"
                                                                type="button"
                                                                style={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    padding: 0,
                                                                }}
                                                                onClick={() =>
                                                                    setSignalField(
                                                                        goal.code,
                                                                        signal.code,
                                                                        {
                                                                            done_count: Math.max(
                                                                                0,
                                                                                sd.done_count - 1
                                                                            ),
                                                                        }
                                                                    )
                                                                }
                                                            >
                                                                -
                                                            </button>
                                                            <span
                                                                className="text-center fw-bold"
                                                                style={{
                                                                    width: 22,
                                                                    fontSize: 13,
                                                                    color:
                                                                        sd.done_count > 0
                                                                            ? goal.theme?.color_primary || '#166534'
                                                                            : '#cbd5e1',
                                                                }}
                                                            >
                                                                {sd.done_count}
                                                            </span>
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary"
                                                                type="button"
                                                                style={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    padding: 0,
                                                                }}
                                                                onClick={() =>
                                                                    setSignalField(
                                                                        goal.code,
                                                                        signal.code,
                                                                        {
                                                                            done_count: Math.min(
                                                                                127,
                                                                                sd.done_count + 1
                                                                            ),
                                                                        }
                                                                    )
                                                                }
                                                            >
                                                                +
                                                            </button>
                                                        </div>

                                                        {/* notes */}
                                                        <input
                                                            className="form-control form-control-sm"
                                                            placeholder="Brief description"
                                                            value={sd.signal_notes}
                                                            onChange={(e) =>
                                                                setSignalField(
                                                                    goal.code,
                                                                    signal.code,
                                                                    {
                                                                        signal_notes:
                                                                            e.target.value,
                                                                    }
                                                                )
                                                            }
                                                            style={{
                                                                fontSize: 11.5,
                                                                padding: '5px 8px',
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* =================== GENERAL CALL NOTES =================== */}
                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body p-4">
                        <label className="form-label fw-semibold">
                            General Call Notes
                        </label>
                        <textarea
                            className="form-control"
                            rows={3}
                            placeholder="Overall call observations, follow-ups, blockers…"
                            value={callNotes}
                            onChange={(e) => setCallNotes(e.target.value)}
                        />
                    </div>
                </div>

                {/* =================== ACTIONS =================== */}
                <div className="d-flex justify-content-end gap-2 mb-5">
                    <button
                        type="button"
                        className="btn btn-outline-secondary px-4"
                        onClick={onClear}
                        disabled={saving}
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary px-4"
                        onClick={onSave}
                        disabled={saving || !adminCallLogId}
                        title={
                            !adminCallLogId
                                ? 'Start a call to attach your edits to a call session'
                                : undefined
                        }
                    >
                        {saving
                            ? 'Saving…'
                            : adminCallLogId
                            ? 'Save Response'
                            : 'Start a call to save'}
                    </button>
                </div>
                  </>
                )}
            </div>

            {/* =================== CLICK-TO-CALL MODAL =================== */}
            {/* Same shape as OutreachLog's modal — Team / Purpose Category /
                Sub Category / Call Mode → POST acefone/click-to-call/. */}
            {showClickToCallModal && (
                <div
                    className="modal d-block"
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Initiate Click-to-Call</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={handleClickToCallModalClose}
                                />
                            </div>
                            <div className="modal-body">
                                <form>
                                    {/* Team */}
                                    <div className="mb-3">
                                        <label className="form-label font-500">
                                            Team <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-select"
                                            value={clickToCallForm.team}
                                            onChange={(e) =>
                                                setClickToCallForm({
                                                    ...clickToCallForm,
                                                    team: e.target.value,
                                                })
                                            }
                                        >
                                            <option value="">Select Team</option>
                                            {(state?.domain?.get('OUTREACH_TEAM_CODE') ?? []).map(
                                                (opt: any) => (
                                                    <option key={opt.code} value={opt.code}>
                                                        {opt.value}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>

                                    {/* Purpose Category */}
                                    <div className="mb-3">
                                        <label className="form-label font-500">
                                            Purpose Category{' '}
                                            <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-select"
                                            value={clickToCallForm.purposeCategory}
                                            onChange={(e) =>
                                                setClickToCallForm({
                                                    ...clickToCallForm,
                                                    purposeCategory: e.target.value,
                                                    purposeSubCategory: '',
                                                })
                                            }
                                        >
                                            <option value="">Select Purpose Category</option>
                                            {(
                                                state?.domain?.get(
                                                    'OUTREACH_PURPOSE_CATEGORY_CODE'
                                                ) ?? []
                                            ).map((opt: any) => (
                                                <option key={opt.code} value={opt.code}>
                                                    {opt.value}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Sub Category */}
                                    <div className="mb-3">
                                        <label className="form-label font-500">
                                            Sub Category <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-select"
                                            value={clickToCallForm.purposeSubCategory}
                                            onChange={(e) =>
                                                setClickToCallForm({
                                                    ...clickToCallForm,
                                                    purposeSubCategory: e.target.value,
                                                })
                                            }
                                            disabled={!clickToCallForm.purposeCategory}
                                        >
                                            <option value="">Select Sub Category</option>
                                            {filteredSubCategories.map((opt: any) => (
                                                <option key={opt.code} value={opt.code}>
                                                    {opt.value}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Call Mode */}
                                    <div className="mb-3">
                                        <label className="form-label font-500">
                                            Call Mode <span className="text-danger">*</span>
                                        </label>
                                        <div className="d-flex gap-3">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    id="cc-extensionMode"
                                                    name="ccCallMode"
                                                    value="extension"
                                                    checked={
                                                        clickToCallForm.callMode === 'extension'
                                                    }
                                                    onChange={(e) =>
                                                        setClickToCallForm({
                                                            ...clickToCallForm,
                                                            callMode: e.target.value,
                                                        })
                                                    }
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="cc-extensionMode"
                                                >
                                                    Extension
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    id="cc-phoneMode"
                                                    name="ccCallMode"
                                                    value="phone"
                                                    checked={
                                                        clickToCallForm.callMode === 'phone'
                                                    }
                                                    onChange={(e) =>
                                                        setClickToCallForm({
                                                            ...clickToCallForm,
                                                            callMode: e.target.value,
                                                        })
                                                    }
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="cc-phoneMode"
                                                >
                                                    Phone
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleClickToCallModalClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleClickToCallFormSubmit}
                                >
                                    Proceed to Call
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </SmartContext.Provider>
    );
};

export default CommitmentCapture;